import { useEffect, useState } from "react";
import { MessageCircle, UserPlus, AlertTriangle, Languages, CalendarCheck, TrendingUp, Cpu } from "lucide-react";
import api from "@/lib/api";
import { AGENT_SETTINGS } from "@/constants/testIds";
import PeriodSelector from "@/components/reports/PeriodSelector";

const LANGUAGE_LABELS = { es: "Español", en: "English" };

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-xs uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-serif text-foreground">{value}</p>
    </div>
  );
}

/** Metricas basicas del agente para el periodo seleccionado: conversaciones,
 * leads nuevos, escaladas a humano e idioma mas usado. */
export default function MetricsTab() {
  const [period, setPeriod] = useState({ period: "month", start: "", end: "" });
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    const params = { period: period.period };
    if (period.period === "custom") { params.start = period.start; params.end = period.end; }
    if (period.period === "custom" && (!period.start || !period.end)) return;
    api.get("/agent/metrics", { params }).then((res) => setMetrics(res.data)).catch(() => setMetrics(null));
  }, [period]);

  return (
    <div className="space-y-6">
      <div data-testid={AGENT_SETTINGS.metricsPeriodSelector}>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {metrics && (
        <>
          <p className="text-xs text-muted-foreground">Periodo: {metrics.period_label}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard icon={MessageCircle} label="Conversaciones" value={metrics.total_conversations} testId={AGENT_SETTINGS.metricsTotalConversations} />
            <StatCard icon={UserPlus} label="Leads nuevos" value={metrics.new_leads} testId={AGENT_SETTINGS.metricsNewLeads} />
            <StatCard icon={AlertTriangle} label="Escaladas a humano" value={metrics.escalated_count} testId={AGENT_SETTINGS.metricsEscalated} />
            <StatCard icon={Languages} label="Idioma mas usado" value={metrics.top_language ? LANGUAGE_LABELS[metrics.top_language] || metrics.top_language : "-"} testId={AGENT_SETTINGS.metricsTopLanguage} />
            <StatCard icon={CalendarCheck} label="Turnos agendados por el agente" value={metrics.appointments_booked} testId={AGENT_SETTINGS.metricsAppointmentsBooked} />
            <StatCard icon={TrendingUp} label="Tasa de conversion" value={`${metrics.conversion_rate}%`} testId={AGENT_SETTINGS.metricsConversionRate} />
          </div>

          {metrics.llm_source === "openai_own" && metrics.ai_usage && (
            <div className="bg-white border border-border rounded-xl p-4" data-testid={AGENT_SETTINGS.metricsAiUsage}>
              <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><Cpu className="w-4 h-4 text-muted-foreground" /> Consumo de la cuenta de OpenAI</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Consultas</p><p className="text-lg font-serif text-foreground">{metrics.ai_usage.requests}</p></div>
                <div><p className="text-xs text-muted-foreground">Con error</p><p className="text-lg font-serif text-foreground">{metrics.ai_usage.failed}</p></div>
                <div><p className="text-xs text-muted-foreground">Tokens enviados</p><p className="text-lg font-serif text-foreground">{metrics.ai_usage.prompt_tokens.toLocaleString("es-AR")}</p></div>
                <div><p className="text-xs text-muted-foreground">Tokens de respuesta</p><p className="text-lg font-serif text-foreground">{metrics.ai_usage.completion_tokens.toLocaleString("es-AR")}</p></div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">El costo en dólares se ve en platform.openai.com, en Usage.</p>
            </div>
          )}

          {metrics.language_breakdown.length > 0 && (
            <div className="bg-white border border-border rounded-xl p-4" data-testid={AGENT_SETTINGS.metricsLanguageBreakdown}>
              <p className="text-sm font-medium text-foreground mb-3">Conversaciones por idioma</p>
              <div className="space-y-2">
                {metrics.language_breakdown.map((l) => (
                  <div key={l.language} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{LANGUAGE_LABELS[l.language] || l.language}</span>
                    <span className="text-foreground font-medium">{l.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
